/**
 * Causali DDT — fonte unica condivisa FE/BE.
 *
 * DDT ATTIVI (scarico / documenti in uscita): il vocabolario rispecchia 1:1
 * l'enum PostgreSQL `pos_ddt_causale`. Il motore delle transizioni a due assi
 * (`resolveSupplyChainTransition`) resta in
 * `packages/backend/src/services/pos/pos-stock-ledger.helpers.ts`, che re-importa
 * questi simboli.
 *
 * DDT PASSIVI (carico merce in ingresso): vocabolario dedicato, persistito in
 * `pos_ddt_passivi.causale` (enum PG `pos_ddt_carico_causale`).
 */

/* ─────────────────────────── DDT attivi (scarico) ─────────────────────────── */

export type DdtCausale =
  | 'vendita' | 'reso' | 'c_visione' | 'c_lavorazione' | 'riparazione'
  | 'omaggio' | 'trasferimento' | 'carico_doa' | 'sostituzione_garanzia'
  | 'consegna_vendita' | 'accompagnamento';

export const DDT_CAUSALI: readonly DdtCausale[] = [
  'vendita', 'reso', 'c_visione', 'c_lavorazione', 'riparazione', 'omaggio', 'trasferimento',
  'carico_doa', 'sostituzione_garanzia', 'consegna_vendita', 'accompagnamento',
];

/** Causali ammesse per lo SCARICO da DDT attivo (esclude `carico_doa`, inbound-only). */
export const DDT_SCARICO_CAUSALI: readonly DdtCausale[] = [
  'vendita', 'reso', 'c_visione', 'c_lavorazione', 'riparazione', 'omaggio', 'trasferimento',
  'sostituzione_garanzia', 'consegna_vendita', 'accompagnamento',
];

/** Causali DDT che NON generano movimenti di stock (`decreaseTx`): la merce è
 *  già stata scaricata da un evento precedente (vendita) o non si muove
 *  affatto (documento di puro accompagnamento). I consumer devono applicare
 *  solo le transizioni di stato risolte dal motore. */
export const DDT_STATUS_ONLY_CAUSALI: readonly DdtCausale[] = [
  'consegna_vendita', 'accompagnamento',
];

/** Causali proponibili nell'emissione MANUALE di un DDT generico (IssueDdtModal).
 *  Esclusioni by-design:
 *  · `trasferimento` — i movimenti tra magazzini si gestiscono dal modulo
 *    Trasferimenti, che genera DDT interno e movimenti in modo atomico (Task #965 F-15);
 *  · `carico_doa` — causale di CARICO (DDT fornitore in ingresso), mai in uscita;
 *  · `sostituzione_garanzia` — emessa dal flusso assistenza, non manualmente;
 *  · `consegna_vendita` / `accompagnamento` — status-only, emesse SOLO
 *    dall'azione "DDT da vendita" sulla vendita di origine. */
export const DDT_CAUSALI_EMISSIONE_MANUALE: readonly DdtCausale[] = [
  'vendita', 'reso', 'c_visione', 'c_lavorazione', 'riparazione', 'omaggio',
];

export const DDT_CAUSALE_LABELS: Record<DdtCausale, string> = {
  vendita: 'Vendita',
  reso: 'Reso',
  c_visione: 'Conto visione',
  c_lavorazione: 'Conto lavorazione',
  riparazione: 'Riparazione',
  omaggio: 'Omaggio',
  trasferimento: 'Trasferimento',
  carico_doa: 'Carico DOA',
  sostituzione_garanzia: 'Sostituzione in garanzia',
  consegna_vendita: 'Consegna vendita',
  accompagnamento: 'Accompagnamento',
};

export function isDdtCausale(v: unknown): v is DdtCausale {
  return typeof v === 'string' && (DDT_CAUSALI as readonly string[]).includes(v);
}

export function isDdtScaricoCausale(v: unknown): v is DdtCausale {
  return typeof v === 'string' && (DDT_SCARICO_CAUSALI as readonly string[]).includes(v);
}

export function isDdtStatusOnlyCausale(v: unknown): v is DdtCausale {
  return typeof v === 'string' && (DDT_STATUS_ONLY_CAUSALI as readonly string[]).includes(v);
}

/** Etichetta leggibile per una causale attiva; ritorna il codice grezzo se sconosciuta. */
export function ddtCausaleLabel(v: string | null | undefined): string {
  if (!v) return '';
  return isDdtCausale(v) ? DDT_CAUSALE_LABELS[v] : v;
}

/* ─────────────────────────── DDT passivi (carico) ─────────────────────────── */

export type DdtCaricoCausale =
  | 'acquisto' | 'reso_da_cliente' | 'omaggio_ricevuto'
  | 'c_lavorazione_rientro' | 'c_visione_rientro'
  | 'rettifica_inventario' | 'trasferimento_in';

export const DDT_CARICO_CAUSALI: readonly DdtCaricoCausale[] = [
  'acquisto', 'reso_da_cliente', 'omaggio_ricevuto',
  'c_lavorazione_rientro', 'c_visione_rientro',
  'rettifica_inventario', 'trasferimento_in',
];

export const DDT_CARICO_CAUSALE_LABELS: Record<DdtCaricoCausale, string> = {
  acquisto: 'Acquisto',
  reso_da_cliente: 'Reso da cliente',
  omaggio_ricevuto: 'Omaggio ricevuto',
  c_lavorazione_rientro: 'Rientro c/lavorazione',
  c_visione_rientro: 'Rientro c/visione',
  rettifica_inventario: 'Rettifica inventario',
  trasferimento_in: 'Trasferimento in entrata',
};

export function isDdtCaricoCausale(v: unknown): v is DdtCaricoCausale {
  return typeof v === 'string' && (DDT_CARICO_CAUSALI as readonly string[]).includes(v);
}

/** Etichetta leggibile per una causale di carico; ritorna il codice grezzo se sconosciuta. */
export function ddtCaricoCausaleLabel(v: string | null | undefined): string {
  if (!v) return '';
  return isDdtCaricoCausale(v) ? DDT_CARICO_CAUSALE_LABELS[v] : v;
}
